import { useCallback, useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../lib/db'
import { syncConversationToCrm, type CrmName, type SyncReport } from '../lib/crm'
import { upsertPersonActivity } from '../lib/personActivity'

export interface ProgressState {
  done: number
  total: number
  phase: 'syncing' | 'complete'
}

const syncLocks = new Set<string>()

export async function syncConversationFromDexie({
  conversationUrn,
  crmName,
  onProgress,
}: {
  conversationUrn: string
  crmName: CrmName
  onProgress?: (progress: ProgressState) => void
}): Promise<SyncReport | null> {
  const lockKey = `${crmName}:${conversationUrn}`
  if (syncLocks.has(lockKey)) return null
  syncLocks.add(lockKey)
  try {
    // The SW's IDB lives in a different origin, so we must fetch from Dexie
    // here and pass the data through the command payload.
    const conversation = await db.conversations.get(conversationUrn)
    if (!conversation) {
      return {
        conversationUrn,
        crmName,
        totalMessages: 0,
        alreadySynced: 0,
        toSync: 0,
        bursts: 0,
        burstsSucceeded: 0,
        burstsFailed: 0,
        errors: [],
        skippedReason: 'conversation_not_found',
        syncedRecords: [],
        runningTotals: null,
        participantProfileUrn: null,
        participantName: null,
        linkedinSlug: null,
      }
    }
    const messages = await db.messages
      .where('conversationUrn')
      .equals(conversationUrn)
      .toArray()

    const report = await syncConversationToCrm({
      crmName,
      conversationUrn,
      conversation: {
        conversationUrn: conversation.conversationUrn,
        participantProfileUrn: conversation.participantProfileUrn ?? null,
        participantName: conversation.participantName,
        isGroup: Boolean(conversation.isGroup),
      },
      messages: messages.map((m) => ({
        messageUrn: m.messageUrn,
        conversationUrn: m.conversationUrn,
        timestamp: m.timestamp,
        isOutgoing: Boolean(m.isOutgoing),
        text: m.text,
        senderName: m.senderName,
      })),
      // Inline reconcile happens below (bulkPut + upsertPersonActivity), so
      // tell the SW not to broadcast — useCrmSyncReconciler handles the
      // sidepanel-direct and SW-auto-sync paths instead.
      notifyOnComplete: false,
    })

    // Mirror the newly-written idempotency rows into Dexie. The service
    // worker's raw IDB write is invisible to Dexie in the page context, so
    // without this the unsyncedCount badge would stay stale until reload.
    if (report?.syncedRecords?.length) {
      try {
        await db.crmMessageSync.bulkPut(report.syncedRecords)
      } catch (e) {
        console.warn('[CRM Sync] Failed to mirror sync records to Dexie:', e)
      }
    }

    // Forward the sync's aggregate totals to Supabase. This used to be 14
    // field writes into Affinity; it's a single atomic RPC now. Non-fatal
    // on failure — the Affinity interactions are already written, the user
    // just won't see updated analytics until the next sync. We log so the
    // issue is visible, but don't disturb the success toast.
    if (report?.runningTotals && report.participantProfileUrn) {
      try {
        await upsertPersonActivity({
          profileUrn: report.participantProfileUrn,
          displayName: report.participantName,
          linkedinSlug: report.linkedinSlug,
          totals: report.runningTotals,
        })
        console.log('[CRM Sync] Supabase person activity upserted for', report.participantProfileUrn)
      } catch (e) {
        console.warn('[CRM Sync] Supabase person activity upsert failed:', e)
      }
    } else {
      console.log(
        '[CRM Sync] Skipped Supabase upsert — reason:',
        !report?.runningTotals ? 'no runningTotals (no new messages to sync)'
          : !report.participantProfileUrn ? 'no participantProfileUrn'
          : 'unknown',
        '| toSync=', report?.toSync, 'alreadySynced=', report?.alreadySynced,
      )
    }

    onProgress?.({ done: report.bursts, total: report.bursts, phase: 'complete' })
    return report
  } finally {
    syncLocks.delete(lockKey)
  }
}

export function useConversationSync(
  conversationUrn: string | null,
  crmName: CrmName | null,
) {
  const [syncing, setSyncing] = useState(false)
  const [progress, setProgress] = useState<ProgressState | null>(null)
  const [lastReport, setLastReport] = useState<SyncReport | null>(null)

  // Unsynced count: count messages in this conversation without a
  // crmMessageSync row. Previously this issued N serial `get(...)` calls
  // — one IDB round-trip per message, which for a 500-message archive
  // conversation meant 500 transactions on every live-query recompute.
  // `bulkGet` does it in one transaction; Dexie fills undefined for
  // missing keys, so `undefined`-count is the unsynced count.
  const unsyncedCount = useLiveQuery(async () => {
    if (!conversationUrn || !crmName) return 0
    const messages = await db.messages
      .where('conversationUrn')
      .equals(conversationUrn)
      .toArray()
    if (messages.length === 0) return 0
    const keys = messages.map((m) => [m.messageUrn, crmName] as [string, string])
    const synced = await db.crmMessageSync.bulkGet(keys)
    let unsynced = 0
    for (const s of synced) if (!s) unsynced++
    return unsynced
  }, [conversationUrn, crmName]) ?? 0

  // Listen for progress messages from the extension
  useEffect(() => {
    if (!conversationUrn) return
    const handler = (event: MessageEvent) => {
      // Same same-window/same-origin guard used across the web app.
      if (event.source !== window) return
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'CRM_SYNC_PROGRESS') return
      if (event.data.conversationUrn !== conversationUrn) return
      setProgress({
        done: event.data.done,
        total: event.data.total,
        phase: event.data.phase,
      })
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [conversationUrn])

  const run = useCallback(async () => {
    if (!conversationUrn || !crmName) return null
    setSyncing(true)
    setProgress({ done: 0, total: 0, phase: 'syncing' })
    try {
      const report = await syncConversationFromDexie({
        conversationUrn,
        crmName,
        onProgress: setProgress,
      })
      if (report) setLastReport(report)
      return report
    } finally {
      setSyncing(false)
      setProgress(null)
    }
  }, [conversationUrn, crmName])

  return { syncing, progress, unsyncedCount, lastReport, run }
}
